
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CandidateMatch } from "./types";

const MatchingTab = () => {
  const matches: CandidateMatch[] = [
    { initials: "SJ", name: "Sarah Johnson", experience: "7 years experience", matchPercentage: 96 },
    { initials: "MB", name: "Michael Brown", experience: "6 years experience", matchPercentage: 92 },
    { initials: "RJ", name: "Robert Jones", experience: "5 years experience", matchPercentage: 85 },
    { initials: "ED", name: "Emma Davis", experience: "4 years experience", matchPercentage: 78 },
    { initials: "DW", name: "David Wilson", experience: "3 years experience", matchPercentage: 71 }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Job Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Job Title</label>
                <div className="p-2 bg-muted rounded-md">Senior Software Engineer</div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Required Skills</label>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline">React.js</Badge>
                  <Badge variant="outline">Node.js</Badge>
                  <Badge variant="outline">TypeScript</Badge>
                  <Badge variant="outline">AWS</Badge>
                  <Badge variant="outline">CI/CD</Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Experience Level</label>
                <div className="p-2 bg-muted rounded-md">5+ years</div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Job Description</label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  We are looking for a Senior Software Engineer to join our product team. The ideal candidate will have strong experience with React.js, Node.js, and TypeScript. Experience with AWS and CI/CD pipelines is a plus.
                </div>
              </div>
              <Button className="w-full">Find Matching Candidates</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Matching Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {matches.map((match) => (
                <div key={match.initials} className="flex items-center justify-between p-3 bg-muted rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-medium">
                      {match.initials}
                    </div>
                    <div>
                      <div className="font-medium">{match.name}</div>
                      <div className="text-sm text-muted-foreground">{match.experience}</div>
                    </div>
                  </div>
                  <Badge className={`bg-${match.matchPercentage >= 90 ? 'green' : match.matchPercentage >= 80 ? 'yellow' : 'orange'}-600`}>
                    {match.matchPercentage}% Match
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Match Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Match Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center font-medium text-lg">
                SJ
              </div>
              <div>
                <div className="text-xl font-bold">Sarah Johnson</div>
                <div className="text-muted-foreground">Senior Software Engineer with 7 years experience</div>
              </div>
              <Badge className="ml-auto bg-green-600">96% Match</Badge>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-2">Skills Match</h3>
              <div className="space-y-2">
                {["React.js", "Node.js", "TypeScript", "AWS", "CI/CD"].map((skill, index) => (
                  <div key={skill}>
                    <div className="flex justify-between mb-1">
                      <div className="text-sm">{skill}</div>
                      <div className="text-sm text-muted-foreground">
                        {["Expert (96%)", "Advanced (90%)", "Advanced (92%)", "Intermediate (75%)", "Intermediate (70%)"][index]}
                      </div>
                    </div>
                    <Progress value={[96, 90, 92, 75, 70][index]} className="h-2" />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-2">Experience Fit</h3>
              <div className="flex items-center gap-4">
                <div className="font-bold text-3xl text-green-600">7+</div>
                <div>
                  <div className="font-medium">Years of Experience</div>
                  <div className="text-sm text-muted-foreground">Exceeds required 5+ years</div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-2">AI Recommendation</h3>
              <div className="p-4 bg-muted rounded-md">
                <p className="text-muted-foreground">
                  Sarah Johnson is an excellent match for the Senior Software Engineer position. Her strong skills in React.js, Node.js, and TypeScript align perfectly with the job requirements. Her 7+ years of relevant experience exceeds the minimum requirement, and her previous work with AWS provides good foundation for the cloud aspects of the role. Recommend scheduling an interview as soon as possible.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline">View Full Profile</Button>
              <Button>Contact Candidate</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MatchingTab;
